import {ChatInput} from '@github-ui/copilot-chat/components/ChatInput'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useCallback, useEffect, useState} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {SkeletonBox} from '@primer/react'
import {ReposSelector} from '@github-ui/repos-selector'
import {RefSelector} from '@github-ui/ref-selector'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {Repository} from '@github-ui/current-repository'
import {useSearchParams} from '@github-ui/react-router'

export interface DropdownRepository {
  id: number
  name: string // This will be nameWithOwner for display in ReposSelector
  nameOnly: string // This will be the original repository name (without owner)
  nameWithOwner: string
  owner: string
  enabled: boolean
  defaultBranch: string
  private: boolean
}

async function queryForRepos(query: string = ''): Promise<DropdownRepository[]> {
  const response = await verifiedFetchJSON('/copilot/agent-sessions/repositories')
  if (response.ok) {
    let repos = (await response.json()).repositories.map(
      (repo: {
        id: number
        nameWithOwner: string
        enabled: boolean
        name: string
        owner: string
        defaultBranch: string
        visibility: string
      }) => {
        return {
          id: repo.id,
          nameWithOwner: repo.nameWithOwner,
          enabled: repo.enabled,
          name: repo.nameWithOwner,
          nameOnly: repo.name,
          owner: repo.owner,
          defaultBranch: repo.defaultBranch,
          private: repo.visibility !== 'public',
        }
      },
    )

    // Apply client-side filtering to search both repository name and nameWithOwner
    if (query.trim()) {
      const lowerQuery = query.toLowerCase()
      repos = repos.filter(
        (repo: DropdownRepository) =>
          repo.nameWithOwner.toLowerCase().includes(lowerQuery) || repo.nameOnly.toLowerCase().includes(lowerQuery),
      )
    }

    return repos
  }
  return []
}

export function NewTaskChatInput({
  handleUserSubmit,
  initialRepo,
  hideRepoSelector,
}: {
  handleUserSubmit: (content: string, selectedRepo: DropdownRepository | null, selectedBranch: string) => Promise<void>
  initialRepo?: Repository | null
  hideRepoSelector?: boolean
}) {
  // Use the chat manager to clear references when a new task is started
  const manager = useChatManager()
  const [selectedRepo, setSelectedRepo] = useState<DropdownRepository | null>(null)
  const [isLoadingRepos, setIsLoadingRepos] = useState(true)
  const [isLoadingBranch, setIsLoadingBranch] = useState(false)
  const isBranchSelectionEnabled = isFeatureEnabled('copilot_immersive_agent_branch_selection')
  const [searchParams] = useSearchParams()
  const branchParam = searchParams.get('branch')
  const [selectedBranch, setSelectedBranch] = useState<string>('')
  // Repo passed down from rails may not have defaultBranch to avoid making an extra db query on server side load
  // we can use the nwo from the initialRepo to set the selectedRepo
  const initialNwo = initialRepo && `${initialRepo.ownerLogin}/${initialRepo.name}`
  const nwoParam = searchParams.get('repo') ?? initialNwo

  useEffect(() => {
    if (initialRepo) {
      const nameWithOwner = `${initialRepo.ownerLogin}/${initialRepo.name}`
      setSelectedRepo({
        id: initialRepo.id,
        owner: initialRepo.ownerLogin,
        name: nameWithOwner,
        nameOnly: initialRepo.name,
        nameWithOwner,
        enabled: true,
        defaultBranch: initialRepo.defaultBranch,
        private: initialRepo.private,
      })
      const initialBranch = branchParam ?? initialRepo?.defaultBranch
      if (initialBranch) {
        setSelectedBranch(initialBranch)
      } else {
        // fetch the default branch from the server async
        const fetchDefaultBranch = async () => {
          try {
            const response = await verifiedFetchJSON(`/_filter/repositories?q=${nameWithOwner}`)
            if (!response.ok) {
              return
            }
            const data = await response.json()
            const repo = data.repositories.find(
              (repoData: {nameWithOwner: string}) => repoData.nameWithOwner === nameWithOwner,
            )
            if (repo) {
              setSelectedBranch(repo.defaultBranch)
            }
          } finally {
            setIsLoadingBranch(false)
          }
        }

        setIsLoadingBranch(true)
        fetchDefaultBranch()
      }
      manager.clearCurrentReferences()
      setIsLoadingRepos(false)
    } else {
      // Fetch the user's repositories as fallback
      const fetchRepos = async () => {
        try {
          const repos = await queryForRepos()

          // If the user passed in an nwo, check that it exists and is enabled
          let repo = nwoParam ? repos.find(r => r.nameWithOwner === nwoParam && r.enabled) : undefined

          // If no repo was found via nwo or nwo wasn't provided, find first enabled repo
          if (!repo) {
            repo = repos.find(r => r.enabled)
          }

          // If we found a repo, set it as the selected repo
          if (repo) {
            setSelectedRepo(repo)
            setSelectedBranch(branchParam ?? repo.defaultBranch)
          }
        } catch {
          // TODO: Show an error to the user that we failed to fetch their repos
        } finally {
          // Always set loading to false after fetching
          setIsLoadingRepos(false)
        }
      }

      fetchRepos()
    }
    // We only want this hook to run once since it triggers a fetch.
    // eslint-disable-next-line react-hooks/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = useCallback(
    async (content: string) => {
      return handleUserSubmit(content, selectedRepo, selectedBranch)
    },
    [handleUserSubmit, selectedRepo, selectedBranch],
  )

  return (
    <ChatInput
      hideAttachmentButton
      placeholder="Describe a coding task to work on"
      size="large"
      onSubmit={onSubmit}
      submitButtonLabel="Start task"
      hideShadow
      extraToolbarLeft={
        <div className="d-flex gap-2 flex-wrap">
          {isLoadingRepos ? (
            <>
              {/* The height should match the populated button height and width should roughly match. */}
              <SkeletonBox height="32px" width="130px" />
            </>
          ) : hideRepoSelector ? null : (
            <ReposSelector
              repositoryLoader={queryForRepos}
              selectionVariant="single"
              selectAllOption={false}
              onSelect={(repo: DropdownRepository | undefined) => {
                if (!repo) return
                setSelectedRepo(repo)
                setSelectedBranch(repo.defaultBranch)
              }}
              currentSelection={selectedRepo || undefined}
              buttonText={selectedRepo?.nameWithOwner || 'Select repository'}
              width="medium"
              additionalButtonProps={{
                'aria-label': `Repository: ${selectedRepo?.nameWithOwner || 'Select repository'}`,
              }}
            />
          )}
          {isBranchSelectionEnabled &&
            (selectedRepo && !isLoadingBranch ? (
              <RefSelector
                // `cacheKey` is something consumers of this component usually get from a key derived from a current repository in the app payload
                // In our case, it's a static string since this page is not aware of any kind of current repository
                cacheKey="agent-sessions-ref-selector"
                canCreate={false}
                currentCommitish={selectedBranch}
                defaultBranch={selectedRepo.defaultBranch}
                owner={selectedRepo.owner}
                repo={selectedRepo.nameOnly}
                types={['branch']}
                selectedRefType="branch"
                closeOnSelect
                hideShowAll
                onSelectItem={ref => {
                  setSelectedBranch(ref)
                }}
              />
            ) : (
              <>
                {/* The height should match the populated button height and width should roughly match. */}
                <SkeletonBox height="32px" width="100px" />
              </>
            ))}
        </div>
      }
      submitDisabled={!selectedRepo || !selectedBranch}
    />
  )
}

try{ NewTaskChatInput.displayName ||= 'NewTaskChatInput' } catch {}